///
/// EarthBrowser
///
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { logger } from '@/utils/LogDispatcher'

/**
 * FileSystemDirectoryHandle exposes an async entries() iterator at runtime,
 * but the DOM lib this project targets doesn't declare it, so type it locally.
 */
type DirectoryHandleWithEntries = FileSystemDirectoryHandle & {
	entries(): AsyncIterableIterator<[string, FileSystemFileHandle | FileSystemDirectoryHandle]>
}

export interface DownloadProgress {
	loaded: number
	total: number
	percentage: number
}

export type ProgressCallback = (progress: DownloadProgress) => void

/**
 * Utility for downloading and managing GeoPackage files in OPFS
 */
export class GeoPackageLoader {
	/**
	 * Downloads a GeoPackage file and saves it to OPFS
	 * @param url URL to download from
	 * @param filename Filename to save as in OPFS
	 * @param onProgress Optional progress callback
	 * @returns The filename that can be used with GeoPackageDatabase
	 */
	static async downloadToOPFS(
		url: string,
		filename: string,
		onProgress?: ProgressCallback
	): Promise<string> {
		try {
			logger.info(`Downloading GeoPackage: ${url}`)

			const response = await fetch(url)
			if (!response.ok) {
				throw new Error(`Failed to download: ${response.statusText}`)
			}

			const total = parseInt(response.headers.get('content-length') || '0')
			const reader = response.body?.getReader()
			if (!reader) {
				throw new Error('No response body')
			}

			// Accumulate chunks
			const chunks: Uint8Array[] = []
			let loaded = 0

			while (true) {
				const { done, value } = await reader.read()
				if (done) break

				chunks.push(value)
				loaded += value.length

				if (onProgress) {
					onProgress({
						loaded,
						total,
						percentage: total > 0 ? (loaded / total) * 100 : 0
					})
				}
			}

			// Combine all chunks
			const data = new Uint8Array(loaded)
			let offset = 0
			for (const chunk of chunks) {
				data.set(chunk, offset)
				offset += chunk.length
			}

		logger.info(`Downloaded ${loaded} bytes, saving to OPFS...`)

		// Write to dedicated geopackages directory (not temp, not VFS)
		// GeoPackages are opened directly from OPFS without VFS
		const opfsRoot = await navigator.storage.getDirectory()
		const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: true })
		const fileHandle = await gpkgDir.getFileHandle(filename, { create: true })
		const writable = await fileHandle.createWritable()
		await writable.write(data)
		await writable.close()

		logger.info(`GeoPackage saved to OPFS: geopackages/${filename}`)

			return filename
		} catch (error) {
			logger.error(`Failed to download GeoPackage: ${String(error)}`)
			throw error
		}
	}

	/**
	 * Check if a GeoPackage file exists in OPFS
	 */
	static async exists(filename: string): Promise<boolean> {
		try {
			const opfsRoot = await navigator.storage.getDirectory()
			const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: false })
			await gpkgDir.getFileHandle(filename)
			return true
		} catch {
			return false
		}
	}

	/**
	 * Delete a GeoPackage file from OPFS
	 */
	static async delete(filename: string): Promise<void> {
		try {
			const opfsRoot = await navigator.storage.getDirectory()
			const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: false })
			await gpkgDir.removeEntry(filename)
			logger.info(`Deleted GeoPackage: ${filename}`)
		} catch (error) {
			logger.warn(`Failed to delete GeoPackage: ${String(error)}`)
			// Don't throw - cleanup failure isn't critical
		}
	}

	/**
	 * List all .gpkg files in OPFS
	 */
	static async list(): Promise<string[]> {
		try {
			const opfsRoot = await navigator.storage.getDirectory()
			const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: false })
			const files: string[] = []

			for await (const [name, handle] of (gpkgDir as DirectoryHandleWithEntries).entries()) {
				if (handle.kind === 'file' && name.endsWith('.gpkg')) {
					files.push(name)
				}
			}

			return files
		} catch {
			// Directory doesn't exist or can't be accessed
			return []
		}
	}

	/**
	 * Get the size of a GeoPackage file in OPFS
	 */
	static async getSize(filename: string): Promise<number> {
		try {
			const opfsRoot = await navigator.storage.getDirectory()
			const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: false })
			const fileHandle = await gpkgDir.getFileHandle(filename)
			const file = await fileHandle.getFile()
			return file.size
		} catch (error) {
			logger.error(`Failed to get GeoPackage size: ${String(error)}`)
			return 0
		}
	}

	/**
	 * Check available storage space
	 */
	static async checkStorage(): Promise<{
		quota: number
		usage: number
		available: number
		percentUsed: number
	}> {
		const estimate = await navigator.storage.estimate()
		const quota = estimate.quota || 0
		const usage = estimate.usage || 0
		const available = quota - usage

		return {
			quota,
			usage,
			available,
			percentUsed: quota > 0 ? (usage / quota) * 100 : 0
		}
	}

	/**
	 * Download a GeoPackage with storage check
	 */
	static async downloadWithStorageCheck(
		url: string,
		filename: string,
		onProgress?: ProgressCallback
	): Promise<string> {
		// Check available storage
		const storage = await this.checkStorage()

		// Get file size from HEAD request
		const response = await fetch(url, { method: 'HEAD' })
		const fileSize = parseInt(response.headers.get('content-length') || '0')

		// Require 10% buffer
		const requiredSpace = fileSize * 1.1

		if (requiredSpace > storage.available) {
			throw new Error(
				`Insufficient storage: need ${Math.round(requiredSpace / 1024 / 1024)}MB, ` +
				`have ${Math.round(storage.available / 1024 / 1024)}MB available`
			)
		}

		return this.downloadToOPFS(url, filename, onProgress)
	}

	/**
	 * Save data directly to OPFS
	 * Used for external files loaded via File System Access API
	 */
	static async saveToOPFS(filename: string, data: Uint8Array): Promise<void> {
		try {
			logger.info(`Saving ${data.length} bytes to OPFS: ${filename}`)

			// Verify this is a valid SQLite file
			if (data.length < 16) {
				throw new Error('File too small to be a valid SQLite database')
			}

			// Check SQLite magic header: "SQLite format 3\0"
			const magic = new TextDecoder().decode(data.slice(0, 16))
			if (!magic.startsWith('SQLite format 3')) {
				throw new Error('Not a valid SQLite database file')
			}

			const opfsRoot = await navigator.storage.getDirectory()
			const gpkgDir = await opfsRoot.getDirectoryHandle('geopackages', { create: true })
			const fileHandle = await gpkgDir.getFileHandle(filename, { create: true })
			const writable = await fileHandle.createWritable()
			await writable.write(new Blob([data.buffer as ArrayBuffer]))
			await writable.close()

			// Verify the file was written correctly
			const verifyHandle = await gpkgDir.getFileHandle(filename)
			const verifyFile = await verifyHandle.getFile()
			const verifySize = verifyFile.size

			if (verifySize !== data.length) {
				throw new Error(`File size mismatch: expected ${data.length}, got ${verifySize}`)
			}

			logger.info(`Saved and verified ${verifySize} bytes to OPFS: geopackages/${filename}`)
		} catch (error) {
			logger.error(`Failed to save to OPFS: ${String(error)}`)
			throw error
		}
	}
}

